<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>組織資料中心</b-breadcrumb-item>
            <b-breadcrumb-item active>參數管理</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-6 mb-2 mb-xl-0 font-weight-bold">
          {{ title }}
        </h4>
        <div
          class="
            col-12 col-xl-6
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="hasCreatePermission()"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            :to="getCreateRoute()"
          >
            <i class="fa fa-plus"></i>新增組織參數
          </b-button>
          <b-form-select
            v-model="query.merchant_id"
            :options="merchants"
            :disabled="isFetchMerchants"
            @change="handleMerchantChange"
            class="mb-2 mb-xl-0 mr-2"
          ></b-form-select>
          <b-input-group>
            <b-form-input
              placeholder="搜尋"
              v-model="query.keyword"
              @keyup.enter="handlePageResetWithoutReload"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="organizationVariables"
                :fields="fields"
              >
                <template #cell(actions)="data">
                  <span v-if="data.item.status === 'enabled'">
                    <b-button
                      v-if="hasModifyPermission()"
                      class="ml-2"
                      variant="inverse-info"
                      :to="getEditSettingRoute(data.item)"
                    >
                      <span class="mdi mdi-settings"></span>
                      設定
                    </b-button>
                    <b-button
                      variant="inverse-primary"
                      :to="getShowRoute(data.item)"
                    >
                      <span class="mdi mdi-eye"></span>
                      查看
                    </b-button>
                    <b-button
                      v-if="hasModifyPermission()"
                      class="ml-2"
                      variant="inverse-warning"
                      :to="getEditRoute(data.item)"
                    >
                      <span class="mdi mdi-lead-pencil"></span>
                      編輯
                    </b-button>
                    <b-button
                      v-if="hasModifyPermission()"
                      class="ml-2"
                      variant="inverse-danger"
                      @click="handleDelete(data.item)"
                    >
                      <span class="mdi mdi-delete"></span>
                      刪除
                    </b-button>
                  </span>
                  <span v-else>
                    <b-button
                      v-if="hasModifyPermission()"
                      variant="primary"
                      @click="handleActivate(data.item)"
                    >
                      開通
                    </b-button>
                  </span>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as consts from "@/consts";
import organizationVariableApi from "../../../apis/organization-variable";
import merchantApi from "@/apis/merchant";
import PermissionChecker from "@/utils/PermissionChecker";
import _ from "lodash";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { mapGetters } from "vuex";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";

export default {
  components: {
    CustomPagination,
  },
  mixins: [paginationMixin],
  data() {
    return {
      consts,
      totalRows: 0,
      showLoading: true,
      showLoadingUpload: false,
      isFetchMerchants: false,
      organizationVariables: [],
      merchantName: "",
      merchants: [{ value: "no_merchant", text: "品牌組織階層" }],
      fields: [
        {
          key: "merchant_name",
          label: "通路",
        },
        {
          key: "name",
          label: "參數名稱",
        },
        {
          key: "key",
          label: "代號",
          sortable: true,
        },
        {
          key: "value",
          label: "內容",
          formatter: (x) =>
            _.isString(x) && x.length > 20 ? x.substring(0, 20) + "..." : x,
        },
        {
          key: "actions",
          label: "操作",
        },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        merchant_id: "no_merchant",
      },
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    ...mapGetters("route", ["routeQuery"]),
    title() {
      const orgName = (this.organization && this.organization.name) || '';
      let displayContent = orgName;
      if (this.merchantName) {
        displayContent = `${orgName} - ${this.merchantName}`;
      }
      const displayTitle = displayContent ? `【${displayContent}】` : '';
      return `${displayTitle}參數管理`;
    },
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    // 組織參數管理元件是由 OrganizationVariableList, OrganizationVariableListByMerchant 共用
    isByMerchant() {
      return !!this.$route.params.merchant_id;
    },
    orgId() {
      return this.$route.params.orgId;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.getOrganizationVariables();
    },
    $route: {
      handler(newRoute, oldRoute) {
        if (!this.initialized) return;
        if (newRoute.name !== oldRoute.name || newRoute.params.merchant_id !== oldRoute.params.merchant_id) {
          if (newRoute.params.merchant_id) {
            this.query.merchant_id = newRoute.params.merchant_id;
          }
          this.updateMerchantName(this.query.merchant_id);
          this.getOrganizationVariables();
        }
      },
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    if (this.$route.params.merchant_id) {
      this.query.merchant_id = this.$route.params.merchant_id;
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await this.getMerchants();
      await this.getOrganizationVariables();
      this.initialized = true;
    },
    async getOrganizationVariables() {
      this.showLoading = true;
      try {
        const params = {
          ...this.query,
          org_code: this.organization.id,
        };
        if (params.merchant_id === "no_merchant") {
          params.no_merchant = true;
          delete params.merchant_id;
        } else if (params.merchant_id === "all_active") {
          params.activated_only = true;
          delete params.merchant_id;
        }

        const { data } = await organizationVariableApi.list(params);
        this.organizationVariables = data.data;
        this.totalRows = data.meta.total;
      } catch (error) {
        console.error(error);
        if (error.status !== 401) {
          this.$swal.fire({
            title: "錯誤",
            type: "error",
            text: "取得組織參數錯誤",
          });
        }
      }
      this.showLoading = false;
    },
    updateMerchantName(merchantId) {
      if (!merchantId || merchantId === "no_merchant" || merchantId === "all_active") {
        this.merchantName = "";
        return;
      }
      const merchant = this.merchants.find((m) => m.value === merchantId);
      if (merchant) {
        this.merchantName = merchant.text.replace(/\s*\(.*\)$/, "");
      }
    },
    handleMerchantChange(merchantId) {
      this.updateMerchantName(merchantId);

      const queryParams = {
        page: 1,
        per_page: this.query.per_page,
        keyword: this.query.keyword || undefined,
        merchant_id: merchantId || undefined,
      };

      if (merchantId && merchantId !== "no_merchant" && merchantId !== "all_active") {
        this.$router.push({
          name: "OrganizationVariableListByMerchant",
          params: { merchant_id: merchantId },
          query: queryParams,
        });
      } else {
        if (this.isByMerchant) {
          this.$router.push({
            name: "OrganizationVariableList",
            query: queryParams,
          });
        } else {
          this.getOrganizationVariables();
        }
      }
    },
    async getMerchants() {
      this.isFetchMerchants = true;
      const { data } = await merchantApi.list();
      this.isFetchMerchants = false;

      if (data.data.length > 0) {
        this.merchants = [
          { value: "all_active", text: "全組織已開通參數" },
          { value: "no_merchant", text: "品牌組織階層" },
          ...data.data.map((merchant) => {
            return {
              value: merchant.id,
              text: merchant.type_name
                ? `${merchant.name} (${merchant.type_name})`
                : merchant.name,
            };
          }),
        ];
      } else {
        this.merchants = [
          { value: "all_active", text: "全組織已開通參數" },
          { value: "no_merchant", text: "品牌組織階層" },
        ];
      }

      this.updateMerchantName(this.query.merchant_id);
    },
    hasCreatePermission() {
      const permission = this.isByMerchant
        ? this.$permissions.consts.ORGANIZATION_MERCHANT_VARIABLE_CREATE
        : this.$permissions.consts.ORGANIZATION_VARIABLE_CREATE;
      return this.$permissions.has(permission);
    },
    hasModifyPermission() {
      const permission = this.isByMerchant
        ? this.$permissions.consts.ORGANIZATION_MERCHANT_VARIABLE_MODIFY
        : this.$permissions.consts.ORGANIZATION_VARIABLE_MODIFY;
      return this.$permissions.has(permission);
    },
    getCreateRoute() {
      return this.isByMerchant
        ? { name: "OrganizationVariableCreateByMerchant" }
        : { name: "OrganizationVariableCreate" };
    },
    getEditSettingRoute(variable) {
      return this.isByMerchant
        ? {
            name: "OrganizationVariableConfigByMerchant",
            params: { organization_variable_id: variable.id },
          }
        : {
            name: "OrganizationVariableConfig",
            params: { organization_variable_id: variable.id },
          };
    },
    getShowRoute(variable) {
      return this.isByMerchant
        ? {
            name: "OrganizationVariableShowByMerchant",
            params: { organization_variable_id: variable.id },
          }
        : {
            name: "OrganizationVariableShow",
            params: { organization_variable_id: variable.id },
          };
    },
    getEditRoute(variable) {
      return this.isByMerchant
        ? {
            name: "OrganizationVariableEditByMerchant",
            params: {
              organization_variable_id: variable.id,
            },
          }
        : {
            name: "OrganizationVariableEdit",
            params: { organization_variable_id: variable.id },
          };
    },
    async handleDelete(variable) {
      await this.$swal
        .fire({
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>Key 名稱：${variable.key}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          type: "warning",
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            this.deleteVariable(variable);
          }
        });
    },
    async deleteVariable(variable) {
      let response = await organizationVariableApi.remove({
        org_code: this.organization.id,
        organizationVariable: variable,
      });
      if (response.status === 200) {
        this.$swal.fire({
          title: "成功",
          type: "success",
          text: "成功刪除 " + variable.key,
        });
        this.getOrganizationVariables();
      } else {
        if (response.data.message) {
          this.$swal.fire({
            title: "錯誤",
            type: "error",
            text: response.data.message,
          });
        }
      }
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    // eslint-disable-next-line no-unused-vars
    async handleActivate(variable) {
      const merchantId = this.query.merchant_id;
      const isValidMerchantId = merchantId && merchantId !== "no_merchant" && merchantId !== "all_active";

      const response = await organizationVariableApi.activate(
        this.organization.id,
        {
          module_config_mapping_id: variable.module_config_mapping_id,
          ...(isValidMerchantId && { merchant_id: merchantId }),
        }
      );
      if (response.status === 200) {
        this.$swal.fire({
          title: "成功",
          type: "success",
          text: "成功開通 " + variable.key,
        });
        this.getOrganizationVariables();
      } else {
        if (response.data.message) {
          this.$swal.fire({
            title: "錯誤",
            type: "error",
            text: response.data.message,
          });
        }
      }
    },
  },
};
</script>

<style scoped>
.btn + .btn {
  margin-left: 0.5rem;
}
</style>
